import { Loader2, RotateCcw, X } from 'lucide-react';
import { POS_Z_CLASS } from '../../utils/posZLayers';
import CaisseCommandeFactureAvoirActions from './CaisseCommandeFactureAvoirActions.jsx';
import { parseSavPaymentReference } from '../../utils/caisseDuplicataRefundDisplay.js';
import {
  aggregateRefundedQtyByLine,
  buildHistoryLineRefundDisplay,
} from '../../utils/caisseSavRefundQty.js';

/**
 * Consultation ticket caisse (lecture seule) — produits, paiements, remboursements, totaux.
 * Aucune action financière ; fermeture sans effet sur la sélection parente.
 */
export default function CaisseTicketConsultationModal({
  open,
  loading,
  ticket,
  fideliteDisplay,
  formatMoney,
  onClose,
  factureAvailable = false,
  factureLoading = false,
  onDownloadFacture,
  onDownloadAvoir,
  avoirLoadingId = null,
}) {
  if (!open) return null;

  const ticketDetail = ticket;
  const isOpen =
    ticketDetail?.statut === 'EN_ATTENTE' && ticketDetail?.statutPaiement === 'EN_ATTENTE';
  const isCancelled = ticketDetail?.statut === 'ANNULEE';
  const statutLabel = isCancelled ? 'Annulé' : isOpen ? 'En attente' : 'Terminé';
  const statutColor = isCancelled ? 'text-red-600' : isOpen ? 'text-orange-600' : 'text-emerald-600';
  const paiementLabel =
    ticketDetail?.statutPaiement === 'VALIDE'
      ? 'Payé'
      : ticketDetail?.statutPaiement === 'REMBOURSE'
        ? 'Remboursé'
        : ticketDetail?.statutPaiement === 'EN_ATTENTE'
          ? 'Non payé'
          : ticketDetail?.statutPaiement || '—';

  const encaissements = (ticketDetail?.paiements || []).filter((p) => p.montant >= 0);
  const remboursements = (ticketDetail?.paiements || []).filter((p) => p.montant < 0);
  const totalRembourse = remboursements.reduce((s, p) => s + Math.abs(p.montant), 0);
  const totalNet = ticketDetail ? ticketDetail.total - totalRembourse : 0;
  const hasRefunds = remboursements.length > 0;
  const refundedByLine = aggregateRefundedQtyByLine(
    ticketDetail?.paiements,
    ticketDetail?.produits
  );

  return (
    <div
      className={`fixed inset-0 ${POS_Z_CLASS.modalBackdrop} flex items-center justify-center bg-black/40 p-4 print:hidden`}
      onClick={onClose}
      data-testid="caisse-ticket-consultation-backdrop"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
        data-testid="caisse-ticket-consultation-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-slate-100 px-4 sm:px-6 py-4 flex items-center justify-between z-10 gap-3">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-slate-900 truncate">
              {loading
                ? 'Chargement…'
                : ticketDetail?.numeroTicket ||
                  ticketDetail?.numero ||
                  (ticketDetail?.id ? ticketDetail.id.slice(-8) : 'Ticket')}
            </h3>
            {ticketDetail?.dateCommande && (
              <p className="text-xs text-slate-400">
                {new Date(ticketDetail.dateCommande).toLocaleString('fr-FR')}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 min-h-[48px] min-w-[48px] flex items-center justify-center rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200"
            aria-label="Fermer la consultation ticket"
            data-testid="caisse-ticket-consultation-close"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {loading || !ticketDetail ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-violet-600" aria-hidden />
          </div>
        ) : (
          <>
            {isCancelled && (
              <div className="bg-red-50 border-b border-red-200 px-6 py-2 text-center">
                <span className="text-sm font-bold text-red-700">TICKET ANNULÉ</span>
              </div>
            )}

            <div className="p-4 sm:p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-xl px-4 py-3">
                  <p className="text-xs text-slate-500">Statut</p>
                  <p className={`font-semibold ${statutColor}`}>{statutLabel}</p>
                </div>
                <div className="bg-slate-50 rounded-xl px-4 py-3">
                  <p className="text-xs text-slate-500">Paiement</p>
                  <p className="font-semibold text-slate-900">{paiementLabel}</p>
                </div>
                {ticketDetail.methodePaiement && ticketDetail.statutPaiement !== 'EN_ATTENTE' && (
                  <div className="bg-slate-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-slate-500">Méthode</p>
                    <p className="font-semibold text-slate-900">{ticketDetail.methodePaiement}</p>
                  </div>
                )}
                {ticketDetail.user && (
                  <div className="bg-slate-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-slate-500">Client</p>
                    <p className="font-semibold text-slate-900 truncate">
                      {ticketDetail.user.prenom} {ticketDetail.user.nom}
                    </p>
                  </div>
                )}
              </div>

              {fideliteDisplay?.phase && (
                <div
                  className={`rounded-xl px-4 py-3 border ${
                    fideliteDisplay.phase === 'intent'
                      ? 'bg-violet-50 border-violet-200'
                      : 'bg-emerald-50 border-emerald-200'
                  }`}
                >
                  <p className="text-xs font-semibold uppercase tracking-wider mb-1 text-slate-600">
                    {fideliteDisplay.phase === 'intent' ? 'Intention fidélité' : 'Récompense appliquée'}
                  </p>
                  <ul className="space-y-0.5">
                    {(fideliteDisplay.lines || []).map((line) => (
                      <li
                        key={line}
                        className={`text-sm ${
                          fideliteDisplay.phase === 'intent' ? 'text-violet-900' : 'text-emerald-900'
                        }`}
                      >
                        {line}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="border-t border-slate-100 pt-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">
                  Articles ({(ticketDetail.produits || []).length})
                </h4>
                <div className="space-y-2">
                  {(ticketDetail.produits || []).map((lp) => {
                    const lineRefund = buildHistoryLineRefundDisplay(lp, refundedByLine);
                    const strikeAll = lineRefund.isFullyRefunded;
                    return (
                      <div
                        key={lp.id}
                        className={`flex justify-between items-start gap-2 rounded-lg px-3 py-2 ${
                          lineRefund.rembourses > 0
                            ? 'bg-orange-50 border border-orange-200'
                            : 'bg-slate-50'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <p
                            className={`text-sm font-medium ${
                              strikeAll ? 'text-orange-400 line-through' : 'text-slate-900'
                            }`}
                          >
                            {lp.produit?.nom || 'Produit'}
                          </p>
                          {lineRefund.isPartiallyRefunded && (
                            <p className="text-xs text-orange-700 mt-0.5">
                              Vendus {lineRefund.vendus} · remboursés {lineRefund.rembourses} ·
                              conservés {lineRefund.conserves}
                            </p>
                          )}
                          {lineRefund.isFullyRefunded && (
                            <span className="text-xs font-semibold text-orange-600">
                              Entièrement remboursé
                            </span>
                          )}
                          {lp.commentaire && (
                            <p className="text-xs text-slate-400 whitespace-pre-line mt-0.5">
                              {lp.commentaire}
                            </p>
                          )}
                          {lp.poidsSouhaite && (
                            <p className="text-xs text-slate-400">
                              {lp.poidsSouhaite}g{lp.typeDecoupe ? ` · ${lp.typeDecoupe}` : ''}
                            </p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm text-slate-500">x{lineRefund.vendus}</p>
                          <p
                            className={`text-sm font-bold ${
                              strikeAll ? 'text-orange-400 line-through' : ''
                            }`}
                          >
                            {formatMoney(
                              lp.prixTotal || lp.prixFinal || lp.prixUnitaire * lp.quantite
                            )}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {encaissements.length > 0 && (
                <div className="border-t border-slate-100 pt-4">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Encaissements</h4>
                  <div className="space-y-1.5">
                    {encaissements.map((p) => (
                      <div
                        key={p.id}
                        className="flex justify-between text-sm bg-emerald-50 rounded-lg px-3 py-2"
                      >
                        <span className="text-slate-700 font-medium">{p.methodePaiement}</span>
                        <span className="font-bold text-emerald-700">{formatMoney(p.montant)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {hasRefunds && (
                <div className="border-t border-orange-200 pt-4">
                  <h4 className="text-sm font-semibold text-orange-700 mb-3 flex items-center gap-1.5">
                    <RotateCcw className="h-4 w-4" />
                    Remboursements ({remboursements.length})
                  </h4>
                  <div className="space-y-1.5">
                    {remboursements.map((p) => {
                      const savMeta = parseSavPaymentReference(p.reference);
                      const typeLabel =
                        savMeta?.type === 'REMBOURSEMENT_COMPLET'
                          ? 'Complet'
                          : savMeta?.type === 'REMBOURSEMENT_ARTICLES'
                            ? 'Par articles'
                            : savMeta?.type === 'REMBOURSEMENT_LIBRE'
                              ? 'Montant libre'
                              : 'Remboursement';
                      return (
                        <div
                          key={p.id}
                          className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2"
                        >
                          <div className="flex justify-between text-sm">
                            <span className="text-orange-800 font-medium">
                              {typeLabel} · {p.methodePaiement}
                            </span>
                            <span className="font-bold text-orange-700">
                              -{formatMoney(Math.abs(p.montant))}
                            </span>
                          </div>
                          {savMeta?.motif && (
                            <p className="text-xs text-orange-600 mt-1">Motif : {savMeta.motif}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="border-t border-slate-200 pt-4 space-y-1">
                {fideliteDisplay?.phase === 'applied' && fideliteDisplay.reductionEur > 0 && (
                  <div className="flex justify-between items-baseline text-sm">
                    <span className="text-emerald-700">
                      Fidélité
                      {fideliteDisplay.libelle ? ` · ${fideliteDisplay.libelle}` : ''}
                    </span>
                    <span className="font-semibold text-emerald-700">
                      −{formatMoney(fideliteDisplay.reductionEur)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-baseline">
                  <span className="text-lg font-bold text-slate-900">Total</span>
                  <span
                    className={`text-2xl font-bold ${
                      isCancelled
                        ? 'text-red-500 line-through'
                        : hasRefunds
                          ? 'text-slate-400 line-through text-lg'
                          : 'text-emerald-600'
                    }`}
                  >
                    {formatMoney(ticketDetail.total)}
                  </span>
                </div>
                {hasRefunds && (
                  <>
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm text-orange-600">Remboursé</span>
                      <span className="text-sm font-bold text-orange-600">
                        -{formatMoney(totalRembourse)}
                      </span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-lg font-bold text-slate-900">Total net</span>
                      <span
                        className={`text-2xl font-bold ${
                          totalNet <= 0 ? 'text-red-500' : 'text-emerald-600'
                        }`}
                      >
                        {formatMoney(totalNet)}
                      </span>
                    </div>
                  </>
                )}
              </div>

              <CaisseCommandeFactureAvoirActions
                commandeId={ticketDetail.id}
                numeroFacture={ticketDetail.numeroFacture}
                avoirs={ticketDetail.avoirs}
                factureAvailable={factureAvailable}
                factureLoading={factureLoading}
                onDownloadFacture={onDownloadFacture}
                onDownloadAvoir={onDownloadAvoir}
                avoirLoadingId={avoirLoadingId}
                formatMoney={formatMoney}
                testIdPrefix="caisse-ticket-consultation"
              />

              <button
                type="button"
                onClick={onClose}
                className="w-full min-h-[52px] py-3 bg-slate-100 text-slate-800 font-semibold rounded-xl hover:bg-slate-200"
                data-testid="caisse-ticket-consultation-back"
              >
                Retour à la sélection
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
